import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { parseAsString, useQueryState } from "nuqs";
import { supabase } from "@/integrations/supabase/client";

export const CAMPAIGN_PURCHASES_QUERY_KEY = "admin-campaign-purchases";
export const PURCHASES_PER_PAGE = 20;

export type PurchaseFilterField = "campaign" | "package" | "name" | "email" | "userId" | "pricePaid" | "creditsGranted";
export type PurchaseSortField = "created_at" | "campaign" | "package" | "buyer" | "price_paid" | "credits_granted";
export type SortDirection = "asc" | "desc";
export type PurchaseFilterOperator = "contains" | "equals" | ">=" | "<=" | ">" | "<";

export interface PurchaseFilterTag {
  id: string;
  field: PurchaseFilterField;
  value: string;
  label: string;
  operator?: PurchaseFilterOperator;
}

const FIELD_LABELS: Record<PurchaseFilterField, string> = {
  campaign: "Campaign",
  package: "Package",
  name: "Buyer",
  email: "Email",
  userId: "User ID",
  pricePaid: "Price Paid",
  creditsGranted: "Credits",
};

const PURCHASE_FILTERS_STORAGE_KEY = "admin-campaign-purchase-filters";
const PURCHASE_SORT_STORAGE_KEY = "admin-campaign-purchase-sort";

const getInitialPurchaseFilters = (): string | null => {
  if (typeof window !== "undefined") {
    return localStorage.getItem(PURCHASE_FILTERS_STORAGE_KEY) || null;
  }
  return null;
};

const getInitialPurchaseSort = (): { field: PurchaseSortField; direction: SortDirection } => {
  if (typeof window !== "undefined") {
    const saved = localStorage.getItem(PURCHASE_SORT_STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // Fall through
      }
    }
  }
  return { field: "created_at", direction: "desc" };
};

// Helper to compare values with operators
const compareWithOperator = (value: number, filterValue: number, operator: PurchaseFilterOperator): boolean => {
  switch (operator) {
    case "equals": return value === filterValue;
    case ">=": return value >= filterValue;
    case "<=": return value <= filterValue;
    case ">": return value > filterValue;
    case "<": return value < filterValue;
    default: return value === filterValue;
  }
};

// Helper to compare strings with operators
const compareStringWithOperator = (value: string, filterValue: string, operator: PurchaseFilterOperator): boolean => {
  switch (operator) {
    case "equals": return value.toLowerCase() === filterValue.toLowerCase();
    case "contains": return value.toLowerCase().includes(filterValue.toLowerCase());
    default: return value.toLowerCase().includes(filterValue.toLowerCase());
  }
};

export interface CampaignPurchase {
  id: string;
  user_id: string;
  package_id: string;
  price_paid: number;
  credits_granted: number;
  stripe_session_id: string | null;
  stripe_payment_intent: string | null;
  created_at: string;
  campaign_packages: {
    name: string;
    credits_amount: number;
    campaigns: {
      name: string;
    } | null;
  } | null;
  profiles: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
  } | null;
}

export const useAdminCampaignPurchases = () => {
  const [currentPage, setCurrentPage] = useState(1);
  
  const initialSort = getInitialPurchaseSort();
  const [sortField, setSortField] = useState<PurchaseSortField>(initialSort.field);
  const [sortDirection, setSortDirection] = useState<SortDirection>(initialSort.direction);

  // Filters stored as JSON string in URL with localStorage fallback
  const [filtersParam, setFiltersParamRaw] = useQueryState<string>(
    "purchaseFilters",
    parseAsString.withOptions({ clearOnDefault: false })
  );

  // Wrapper to persist to localStorage
  const setFiltersParam = useCallback((value: string | null) => {
    if (value) {
      localStorage.setItem(PURCHASE_FILTERS_STORAGE_KEY, value);
    } else {
      localStorage.removeItem(PURCHASE_FILTERS_STORAGE_KEY);
    }
    setFiltersParamRaw(value);
  }, [setFiltersParamRaw]);

  // Initialize filters from localStorage on mount (only once)
  useEffect(() => {
    if (filtersParam === null) {
      const stored = getInitialPurchaseFilters();
      if (stored) {
        setFiltersParamRaw(stored);
      }
    }
  }, []); // Only run on mount

  // Parse filters from URL param into FilterTag array
  const filterTags = useMemo<PurchaseFilterTag[]>(() => {
    if (!filtersParam) return [];
    try {
      const parsed = JSON.parse(filtersParam) as { field: PurchaseFilterField; value: string; operator?: PurchaseFilterOperator }[];
      return parsed.map((f, i) => ({
        id: `${f.field}-${i}-${f.value}-${f.operator || "default"}`,
        field: f.field,
        value: f.value,
        label: FIELD_LABELS[f.field],
        operator: f.operator,
      }));
    } catch {
      return [];
    }
  }, [filtersParam]);

  // Add a new filter tag
  const addFilter = useCallback((field: PurchaseFilterField, value: string, operator?: PurchaseFilterOperator) => {
    const current = filterTags.map(t => ({ field: t.field, value: t.value, operator: t.operator }));
    const exists = current.some(f => f.field === field && f.value.toLowerCase() === value.toLowerCase() && f.operator === operator);
    if (exists) return;
    const updated = [...current, { field, value, operator }];
    setFiltersParam(JSON.stringify(updated));
  }, [filterTags, setFiltersParam]);

  // Remove a filter tag by id
  const removeFilter = useCallback((id: string) => {
    const updated = filterTags
      .filter(t => t.id !== id)
      .map(t => ({ field: t.field, value: t.value, operator: t.operator }));
    setFiltersParam(updated.length > 0 ? JSON.stringify(updated) : null);
  }, [filterTags, setFiltersParam]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setFiltersParam(null);
  }, [setFiltersParam]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filtersParam]);

  const handleSort = useCallback((field: PurchaseSortField) => {
    let newDirection: SortDirection;
    if (sortField === field) {
      newDirection = sortDirection === "asc" ? "desc" : "asc";
    } else {
      newDirection = "desc";
    }
    setSortField(field);
    setSortDirection(newDirection);
    localStorage.setItem(PURCHASE_SORT_STORAGE_KEY, JSON.stringify({ field, direction: newDirection }));
    setCurrentPage(1);
  }, [sortField, sortDirection]);

  const { data: purchases, isLoading } = useQuery({
    queryKey: [CAMPAIGN_PURCHASES_QUERY_KEY],
    queryFn: async () => {
      // Paginate through all purchases (Supabase default limit is 1000)
      const allPurchases: any[] = [];
      const pageSize = 1000;
      let from = 0;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from("campaign_purchases")
          .select(`
            *,
            campaign_packages (
              name,
              credits_amount,
              campaigns (name)
            )
          `)
          .order("created_at", { ascending: false })
          .range(from, from + pageSize - 1);

        if (error) throw error;
        if (data && data.length > 0) {
          allPurchases.push(...data);
          from += pageSize;
          hasMore = data.length === pageSize;
        } else {
          hasMore = false;
        }
      }

      if (!allPurchases.length) return [];

      // Get unique user IDs and fetch profiles separately (also paginate if needed)
      const userIds = [...new Set(allPurchases.map(p => p.user_id))];
      
      // Fetch profiles in batches if there are many users
      const profilesData: any[] = [];
      const batchSize = 500; // Supabase has limits on IN clause size
      for (let i = 0; i < userIds.length; i += batchSize) {
        const batch = userIds.slice(i, i + batchSize);
        const { data, error } = await supabase
          .from("profiles")
          .select("id, first_name, last_name, email, phone")
          .in("id", batch);

        if (error) throw error;
        if (data) profilesData.push(...data);
      }

      // Map profiles to purchases
      const profilesMap = new Map(profilesData.map(p => [p.id, p]));
      return allPurchases.map(purchase => ({
        ...purchase,
        profiles: profilesMap.get(purchase.user_id) || null,
      })) as CampaignPurchase[];
    },
  });

  const isEmpty = !isLoading && !purchases?.length;

  // Client-side filtering and sorting
  const sortedPurchases = useMemo(() => {
    if (!purchases?.length) return [];

    let filtered = [...purchases];

    // Group filters by field for OR logic within same field
    const filtersByField = filterTags.reduce((acc, tag) => {
      if (!acc[tag.field]) acc[tag.field] = [];
      acc[tag.field].push({ value: tag.value.toLowerCase(), operator: tag.operator });
      return acc;
    }, {} as Record<PurchaseFilterField, { value: string; operator?: PurchaseFilterOperator }[]>);

    if (filtersByField.campaign?.length) {
      filtered = filtered.filter(p => 
        filtersByField.campaign.some(f => 
          compareStringWithOperator(p.campaign_packages?.campaigns?.name || "", f.value, f.operator || "contains")
        )
      );
    }

    if (filtersByField.package?.length) {
      filtered = filtered.filter(p => 
        filtersByField.package.some(f => 
          compareStringWithOperator(p.campaign_packages?.name || "", f.value, f.operator || "contains")
        )
      );
    }

    if (filtersByField.name?.length) {
      filtered = filtered.filter(p => {
        const fullName = `${p.profiles?.first_name || ""} ${p.profiles?.last_name || ""}`;
        return filtersByField.name.some(f => 
          compareStringWithOperator(fullName, f.value, f.operator || "contains")
        );
      });
    }

    if (filtersByField.email?.length) {
      filtered = filtered.filter(p => 
        filtersByField.email.some(f => 
          compareStringWithOperator(p.profiles?.email || "", f.value, f.operator || "contains")
        )
      );
    }

    if (filtersByField.userId?.length) {
      filtered = filtered.filter(p => 
        filtersByField.userId.some(f => 
          compareStringWithOperator(p.user_id || "", f.value, f.operator || "contains")
        )
      );
    }

    if (filtersByField.pricePaid?.length) {
      filtered = filtered.filter(p => 
        filtersByField.pricePaid.some(f => 
          compareWithOperator(p.price_paid || 0, parseFloat(f.value), f.operator || "equals")
        )
      );
    }

    if (filtersByField.creditsGranted?.length) {
      filtered = filtered.filter(p => 
        filtersByField.creditsGranted.some(f => 
          compareWithOperator(p.credits_granted || 0, parseInt(f.value), f.operator || "equals")
        )
      );
    }

    // Sort
    const sorted = filtered.sort((a, b) => {
      let aVal: string | number | null;
      let bVal: string | number | null;

      switch (sortField) {
        case "created_at":
          aVal = a.created_at ? new Date(a.created_at).getTime() : 0;
          bVal = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        case "campaign":
          aVal = a.campaign_packages?.campaigns?.name?.toLowerCase() || "";
          bVal = b.campaign_packages?.campaigns?.name?.toLowerCase() || "";
          break;
        case "package":
          aVal = a.campaign_packages?.name?.toLowerCase() || "";
          bVal = b.campaign_packages?.name?.toLowerCase() || "";
          break;
        case "buyer":
          aVal = `${a.profiles?.first_name || ""} ${a.profiles?.last_name || ""}`.trim().toLowerCase();
          bVal = `${b.profiles?.first_name || ""} ${b.profiles?.last_name || ""}`.trim().toLowerCase();
          break;
        case "price_paid":
          aVal = a.price_paid || 0;
          bVal = b.price_paid || 0;
          break;
        case "credits_granted":
          aVal = a.credits_granted || 0;
          bVal = b.credits_granted || 0;
          break;
        default:
          return 0;
      }

      if (aVal === bVal) return 0;
      if (sortDirection === "asc") {
        return aVal < bVal ? -1 : 1;
      }
      return aVal > bVal ? -1 : 1;
    });

    return sorted;
  }, [purchases, sortField, sortDirection, filterTags]);

  const totalPurchases = sortedPurchases.length;
  const totalPages = Math.ceil(totalPurchases / PURCHASES_PER_PAGE);
  const paginatedPurchases = sortedPurchases.slice(
    (currentPage - 1) * PURCHASES_PER_PAGE,
    currentPage * PURCHASES_PER_PAGE
  );

  return {
    purchases: paginatedPurchases,
    isLoading,
    isEmpty,
    // Tag-based filters
    filterTags,
    addFilter,
    removeFilter,
    clearAllFilters,
    // Pagination
    currentPage,
    setCurrentPage,
    totalPages,
    totalPurchases,
    // Sorting
    sortField,
    sortDirection,
    handleSort,
  };
};
